import type { GatsbyBrowser } from 'gatsby';

import { routes } from './src/shared/lib/routes';

const NO_SMOOTH_SCROLL = 'no-smooth-scroll';

function normalizePathname(pathname?: string) {
  if (!pathname) return '';
  return pathname.replace(/\/+$/, '') || '/';
}

function isNewsListingNav(prevPath: string, nextPath: string) {
  return prevPath === routes.news && nextPath === routes.news;
}

function jumpToTop() {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

function restoreSmoothScroll() {
  document.documentElement.classList.remove(NO_SMOOTH_SCROLL);
}

function parseHashId(hash: string) {
  const raw = hash.replace(/^#/, '');
  if (!raw) return { id: '', isMangled: false };
  const id = raw.split('?')[0].trim();
  return { id, isMangled: raw.includes('?') };
}

function scrollToHash(hash: string) {
  const { id, isMangled } = parseHashId(hash);
  if (!id) return false;
  const el = document.getElementById(id);
  if (!el) return false;

  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth';
  el.scrollIntoView({ behavior, block: 'start' });

  if (isMangled) {
    window.history.replaceState(null, '', `#${id}`);
  }
  return true;
}

function scrollToHashWithRetry(hash: string) {
  if (scrollToHash(hash)) return;
  requestAnimationFrame(() => {
    if (scrollToHash(hash)) return;
    window.setTimeout(() => scrollToHash(hash), 250);
    window.setTimeout(() => scrollToHash(hash), 750);
  });
}

/** Drop in-flight jump-to-top if a newer navigation (e.g. hash) starts. */
let scrollGen = 0;

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }
};

export const onPreRouteUpdate: GatsbyBrowser['onPreRouteUpdate'] = ({
  location,
  prevLocation,
}) => {
  if (!prevLocation) return;
  scrollGen += 1;

  const prevPath = normalizePathname(prevLocation.pathname);
  const nextPath = normalizePathname(location.pathname);
  if (isNewsListingNav(prevPath, nextPath)) return;
  if (location.hash) {
    restoreSmoothScroll();
    return;
  }
  document.documentElement.classList.add(NO_SMOOTH_SCROLL);
};

export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({
  prevRouterProps,
  routerProps,
}) => {
  const prevPath = normalizePathname(prevRouterProps?.location?.pathname);
  const nextPath = normalizePathname(routerProps.location.pathname);

  if (isNewsListingNav(prevPath, nextPath)) return false;
  // Hash targets are scrolled in onRouteUpdate (smooth scrollIntoView).
  if (routerProps.location.hash) return false;
  return [0, 0];
};

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = ({
  location,
  prevLocation,
}) => {
  const prevPath = normalizePathname(prevLocation?.pathname);
  const nextPath = normalizePathname(location.pathname);

  if (isNewsListingNav(prevPath, nextPath)) {
    restoreSmoothScroll();
    return;
  }

  if (location.hash) {
    restoreSmoothScroll();
    scrollToHashWithRetry(location.hash);
    return;
  }

  jumpToTop();

  const gen = scrollGen;
  // Reach Router focuses #gatsby-focus-wrapper on the next frame, which
  // scrolls the page down a few pixels. Reset after that, then restore
  // CSS smooth-scroll for in-page hashes.
  requestAnimationFrame(() => {
    if (gen !== scrollGen) return;
    document
      .getElementById('gatsby-focus-wrapper')
      ?.focus({ preventScroll: true });
    jumpToTop();
    requestAnimationFrame(() => {
      if (gen !== scrollGen) return;
      jumpToTop();
      restoreSmoothScroll();
    });
  });
};
